import { useParams } from 'react-router-dom';
import React, { useLayoutEffect, useEffect, useState, useContext, useCallback, useMemo } from 'react';
import store from 'store';
import { Text, Image, Button } from 'rebass';
import useSWR from 'swr';
import { toggleNewStoryVisible } from 'stories/actions';
import { useDispatch } from 'react-redux';
import defaultPhotoPreview from '../assets/Default_PreviewPhoto-Rounded.png'
import { getResourcePath } from 'collections/api';
import useEntity from 'entities/hooks/use-entity';
import Wrapper from './Wrapper';
import { getPublicChildJournal } from 'child-journals/api';
import { CurrentCollectionContext } from 'collections/current-collection-context';

const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const isValidId = (id) => regex.test(id);

const DEFAULT_REQUEST_ID = 'cb6310b3-46ef-4373-bc3f-6db5e8be5395';
const DEFAULT_IMAGE_ID = 'https://d1thxwlajbvmyo.cloudfront.net/downloads/5a21e3e1-7682-4894-b2c1-8386661b62f0_small_iphone7.png';
const DEFAULT_USER_NAME = "Emma";

const buildPath = (memoirStoryRequestId) => {
    return isValidId(memoirStoryRequestId)
        ? getResourcePath({
            id: memoirStoryRequestId,
            type: 'child_journal_story_requests',
        })
        : null;
};

const extractImageDetails = (response) => {

    const { data, included } = response.data?.initial;
    const avatarData = data?.relationships?.['subject-avatar']?.data;

    if (!avatarData) return null;

    const image = included.find(
        ({ id, type }) => id === avatarData.id && type === avatarData.type
    );

    if (image?.relationships?.['canvas-image']?.data) {
        const canvasData = image.relationships['canvas-image'].data;
        return included.find(
            ({ id, type }) => id === canvasData.id && type === canvasData.type
        );
    }

    return image;
};

const TellAStory = () => {
    const { memoirStoryRequestId, ref_source } = useParams();
    const dispatch = useDispatch();
    const { setCurrentCollection } = useContext(CurrentCollectionContext);

    if (!isValidId(memoirStoryRequestId)) {
        return null;
    }

    const path = useMemo(() => buildPath(memoirStoryRequestId), [memoirStoryRequestId]);

    const { data, revalidate } = useSWR(store.get('auth_token') ? path + '?shallow=true' : null, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        dedupingInterval: 60000,
    });

    const [journalDetails, setJournalDetails] = useState(null);
    const [imageDetails, setImageDetails] = useState(null);

    const memoirStoryRequest = useMemo(
        () => data?.data?.data?.relationships,
        [data]
    );

    const memoirDetailId = useMemo(
        () => memoirStoryRequest?.['child-journal']?.data?.id,
        [memoirStoryRequest]
    );

    const { member } = useEntity(memoirStoryRequest?.['creator']?.data);

    const isFromMarcomSite = ref_source === 'marcom';

    useEffect(() => {
        if (store.get('auth_token')) {
            revalidate();
        }
    }, [store.get('auth_token')]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

        const refParam = urlParams.get('utm_source');
        const hashRef = hashParams.get('ref');

        if (isFromMarcomSite || refParam === 'marcom' || hashRef === 'marcom') {
            sessionStorage.setItem('fromMarcom', 'true');
        } else
            sessionStorage.removeItem('fromMarcom');
    }, []);

    const getMemoirDetails = useCallback(async (detailId) => {
        if (!detailId) return;

        try {
            const response = await getPublicChildJournal(detailId, '?shallow=true');
            const { type, id } = response.data.initial.data;

            const image = extractImageDetails(response);
            setImageDetails(image);
            setJournalDetails(response.data.initial.data.attributes);
            setCurrentCollection({ type, id });
        } catch (error) {
            console.error('Error fetching memoir details:', error);
        }
    }, [setCurrentCollection]);

    useEffect(() => {
        getMemoirDetails(memoirDetailId);
    }, [memoirDetailId, getMemoirDetails]);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const subjectName = useMemo(() => memoirStoryRequestId === DEFAULT_REQUEST_ID ? DEFAULT_USER_NAME : member?.['first-name'] || '', [member, memoirStoryRequestId, DEFAULT_REQUEST_ID]);

    const imageUrl = useMemo(
        () => imageDetails?.attributes?.['image-versions'][8].url || (memoirStoryRequestId === DEFAULT_REQUEST_ID ? DEFAULT_IMAGE_ID : defaultPhotoPreview),
        [imageDetails, memoirStoryRequestId, DEFAULT_REQUEST_ID]
    );

    const title = useMemo(() => {
        if (memoirStoryRequestId === DEFAULT_REQUEST_ID) return `Be a Part of ${DEFAULT_USER_NAME}'s Book!`;
        if (isFromMarcomSite) return 'See How Easy It Is to Create a Memory';
        if (journalDetails?.['first-name']) return `Be a Part of ${journalDetails['first-name']}'s Book!`;
        return '';
    }, [isFromMarcomSite, journalDetails, memoirStoryRequestId, DEFAULT_REQUEST_ID]);

    const description = useMemo(() => {
        if (isFromMarcomSite) {
            return 'We will not save any of your information.';
        }
        return `After adding a date and title, tap Finish and ${subjectName} will be notified.`;
    }, [isFromMarcomSite, subjectName]);

    // ✅ Memoized callbacks
    const handleStartStory = useCallback(() => {
        dispatch(toggleNewStoryVisible(true));
    }, [dispatch]);

    return (
        <Wrapper p={2} justifyContent="center" alignItems="center">
            <Image
                src={imageUrl}
                minHeight={150}
                minWidth={150}
                height={150}
                width={150}
                maxWidth="150px"
                sx={{ borderRadius: '50%', objectFit: 'cover' }}
                loading="lazy"
            />

            <Text
                color="codGrey"
                fontFamily="Bitter"
                fontSize="30px"
                fontStyle="normal"
                fontWeight={600}
                lineHeight="120%"
                letterSpacing="-0.18px"
                mt="20px"
            >
                {title}
            </Text>

            <Text
                color="#64748B"
                fontSize={16}
                fontFamily="Poppins"
                lineHeight="150%"
                fontWeight={400}
                letterSpacing="-0.096px"
                textAlign="left"
                my={2}
            >
                {!isFromMarcomSite && (
                    <>
                        Share a memory you have of {memoirStoryRequestId === DEFAULT_REQUEST_ID ? DEFAULT_USER_NAME : journalDetails?.['first-name']} — be it a moment, a milestone or a long story.
                        <br />
                        <br />
                    </>
                )}
                Your stories can include any combination of:
                <ul>
                    <li>Voice or video narrations</li>
                    <li>Photo and video uploads</li>
                    <li>Writing and captions</li>
                </ul>
                {description}
            </Text>

            <Button
                mt={2}
                mb={[8, 4]}
                py="unset"
                height="40px"
                onClick={handleStartStory}
                sx={{ borderRadius: '23px' }}
            >
                {isFromMarcomSite ? 'Get Started' : 'Start Your Story'}
            </Button>
        </Wrapper>
    );
};

export default React.memo(TellAStory);